import { type HTMLAttributes, type ReactNode, useRef } from 'react';

import { CheckmarkCircleIc, DividerBulletOutlineIc } from '@dsch/dd-icons';

import { Panel } from '../Panel/Panel';
import { Accordion } from '../../../../../ToolkitV2/Accordion/Accordion';

import { Header } from './CollapsiblePanel.styled';

import { pushToDataLayer } from '../../../../../../services/gtmService';
import { theme } from '../../../../../../helpers/Theme';
import { Spacing } from '../../../../../../helpers/genericStyles';
import { useCollapsiblePanelContext } from './CollapsiblePanel.context';
import { PANEL_STATUS, type PanelStatus } from './panelStatuses';
import {
  usePanelState,
  usePanelDispatch,
  PANEL_ORDER,
} from '../../../../financeProviders/shared/PanelContext';
import { CollapsiblePanelNames } from '../../../../queries/schema/carFinance.schema';

type Props = HTMLAttributes<HTMLElement> &
  Spacing & {
    headerText: ReactNode;
    children: ReactNode;
    accordionName: string;
    panelName: CollapsiblePanelNames;
  };

const CollapsiblePanel = (props: Props) => {
  const {
    headerText,
    mb,
    children,
    accordionName,
    'aria-label': ariaLabel,
    panelName,
  } = props;
  const { status } = useCollapsiblePanelContext();
  const panelState = usePanelState();
  const dispatch = usePanelDispatch();
  const sectionIndex = PANEL_ORDER.indexOf(panelName);
  const hasBeenTracked = useRef(false);

  const handleChange = () => {
    if (!panelState[panelName].isOpen && !hasBeenTracked.current) {
      pushToDataLayer({
        event: 'form_section_opened',
        section_name: accordionName,
        section_index: sectionIndex,
      });
      hasBeenTracked.current = true;
    }
    dispatch({ type: 'TOGGLE_PANEL', panel: panelName });
  };

  const panelStatusIcon = (status: PanelStatus) => {
    return status === PANEL_STATUS.COMPLETE ? (
      <CheckmarkCircleIc
        color={theme.colors.GREEN}
        aria-live="polite"
        aria-label={`${accordionName} complete`}
        role="img"
      />
    ) : (
      <DividerBulletOutlineIc
        color={theme.colors.BLUE}
        aria-label={`${accordionName} incomplete`}
        role="img"
      />
    );
  };

  return (
    <Panel mb={mb} aria-label={ariaLabel}>
      <Accordion
        header={
          <Header>
            {panelStatusIcon(status)}
            {headerText}
          </Header>
        }
        content={children}
        showDivider={false}
        headerSpacerTop={null}
        headerSpacerBottom={null}
        bodySpacer={null}
        accordionName={accordionName}
        isOpen={panelState[panelName].isOpen}
        onChange={handleChange}
      />
    </Panel>
  );
};

export { CollapsiblePanel };
